import type { ReactNode, SVGAttributes } from "react";

export interface IconProps
  extends Omit<SVGAttributes<SVGSVGElement>, "name" | "stroke"> {
  name: string;
  size?: number;
  stroke?: number;
}

/**
 * Stroke-based 24x24 icon set. Ported 1:1 from the React reference
 * (see core.jsx in the design bundle) plus aliases for the canonical
 * names used across the spec: chevron-up/down/left/right, log-out,
 * home, package, users, credit-card, file-text, alert-triangle,
 * arrow-left, arrow-right, check-circle, menu.
 * Any unknown name renders a square with a "?" fallback glyph.
 */
const ICONS: Record<string, ReactNode> = {
  // === navigation / chrome ===
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </>
  ),
  catalog: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>
  ),
  cart: (
    <>
      <path d="M3 4h2l2.5 11h10l2-8H6" />
      <circle cx="9" cy="20" r="1.5" />
      <circle cx="17" cy="20" r="1.5" />
    </>
  ),
  orders: (
    <>
      <path d="M5 4h10l4 4v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
      <path d="M15 4v4h4" />
      <path d="M8 12h8M8 16h5" />
    </>
  ),
  account: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1-4 4.5-6 8-6s7 2 8 6" />
    </>
  ),
  invoices: (
    <>
      <path d="M6 3h9l3 3v15H6z" />
      <path d="M9 9h6M9 13h6M9 17h4" />
    </>
  ),

  // === actions / glyphs ===
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  check: <path d="m5 12 5 5 9-10" />,
  x: <path d="m6 6 12 12M18 6 6 18" />,
  filter: <path d="M4 5h16M7 12h10M10 19h4" />,
  grid: (
    <>
      <rect x="4" y="4" width="7" height="7" />
      <rect x="13" y="4" width="7" height="7" />
      <rect x="4" y="13" width="7" height="7" />
      <rect x="13" y="13" width="7" height="7" />
    </>
  ),
  list: <path d="M4 6h16M4 12h16M4 18h16" />,
  bell: (
    <>
      <path d="M6 16V10a6 6 0 1 1 12 0v6l2 2H4z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </>
  ),
  logout: (
    <>
      <path d="M10 4H5v16h5" />
      <path d="M15 9l3 3-3 3" />
      <path d="M8 12h10" />
    </>
  ),
  download: (
    <>
      <path d="M12 4v12M6 12l6 6 6-6" />
      <path d="M4 20h16" />
    </>
  ),
  repeat: (
    <>
      <path d="M4 8V6a2 2 0 0 1 2-2h11l3 3v5" />
      <path d="m17 11 3-3" />
      <path d="M20 16v2a2 2 0 0 1-2 2H7l-3-3v-5" />
      <path d="m7 13-3 3" />
    </>
  ),
  truck: (
    <>
      <rect x="2" y="7" width="13" height="10" rx="1" />
      <path d="M15 10h4l3 3v4h-7z" />
      <circle cx="7" cy="19" r="2" />
      <circle cx="18" cy="19" r="2" />
    </>
  ),
  pin: (
    <>
      <path d="M12 22s7-7 7-13a7 7 0 0 0-14 0c0 6 7 13 7 13z" />
      <circle cx="12" cy="9" r="2.5" />
    </>
  ),
  card: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="1" />
      <path d="M3 10h18M7 15h4" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 1-1 1.7M12 17h.01" />
    </>
  ),
  phone: (
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a1 1 0 0 1-1 1A15 15 0 0 1 4 5a1 1 0 0 1 1-1z" />
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="1" />
      <path d="m3 7 9 6 9-6" />
    </>
  ),
  star: (
    <path d="m12 3 2.8 6 6.2.5-4.7 4 1.5 6.5L12 16.8 6.2 20l1.5-6.5L3 9.5 9.2 9z" />
  ),
  warn: (
    <>
      <path d="M12 3 2 21h20z" />
      <path d="M12 10v5M12 18h.01" />
    </>
  ),
  sparkle: <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />,
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeoff: (
    <>
      <path d="M4 4l16 16" />
      <path d="M10.6 6.2A9 9 0 0 1 22 12s-1.2 2.4-3.6 4.4M6.5 7.4C3.4 9 2 12 2 12s3.5 7 10 7c1.7 0 3.2-.4 4.6-1" />
      <path d="M9.5 10.5a3 3 0 0 0 4 4" />
    </>
  ),
  arrowup: <path d="M12 19V5M6 11l6-6 6 6" />,
  arrowdown: <path d="M12 5v14M6 13l6 6 6-6" />,
  link: (
    <>
      <path d="M10 14a4 4 0 0 1 0-6l3-3a4 4 0 0 1 6 6l-1 1" />
      <path d="M14 10a4 4 0 0 1 0 6l-3 3a4 4 0 0 1-6-6l1-1" />
    </>
  ),
  coffee: (
    <>
      <path d="M4 8h12v6a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4z" />
      <path d="M16 10h2a2 2 0 0 1 0 6h-2" />
      <path d="M7 3v2M10 3v2M13 3v2" />
    </>
  ),
  box: (
    <>
      <path d="m3 7 9-4 9 4v10l-9 4-9-4z" />
      <path d="m3 7 9 4 9-4M12 11v10" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 14.4a7.7 7.7 0 0 0 0-4.8l2-1.6-2-3.4-2.4.8a7.8 7.8 0 0 0-4.2-2.4L12 .5l-4 2 .8 2.4a7.8 7.8 0 0 0-4.2 2.4L2.6 6.6l-2 3.4L2.6 12a7.7 7.7 0 0 0 0 4.8l-2 1.6 2 3.4 2.4-.8a7.8 7.8 0 0 0 4.2 2.4L12 23.5l4-2-.8-2.4a7.8 7.8 0 0 0 4.2-2.4z" />
    </>
  ),

  // === extras required by the task spec but not in the React source ===
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  "arrow-right": (
    <>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </>
  ),
  "arrow-left": (
    <>
      <path d="M19 12H5" />
      <path d="m11 18-6-6 6-6" />
    </>
  ),
  "check-circle": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12 3 3 5-6" />
    </>
  ),
};

/**
 * Aliases — map the canonical spec names to the icon names that already
 * exist in the React reference. Keeps every consumer naming convention
 * working without duplicating SVG payloads.
 */
const ALIASES: Record<string, string> = {
  "chevron-up": "arrowup",
  "chevron-down": "arrowdown",
  "chevron-left": "arrowup", // semantic fallback — overridden below by real left/right
  "chevron-right": "arrowdown",
  "log-out": "logout",
  home: "dashboard",
  package: "box",
  users: "account",
  "credit-card": "card",
  "file-text": "invoices",
  "alert-triangle": "warn",
};

// Replace the rough chevron-left/right aliases with proper directional glyphs.
ICONS["chevron-left"] = <path d="m15 6-6 6 6 6" />;
ICONS["chevron-right"] = <path d="m9 6 6 6-6 6" />;
ICONS["chevron-up"] = <path d="m6 15 6-6 6 6" />;
ICONS["chevron-down"] = <path d="m6 9 6 6 6-6" />;

delete ALIASES["chevron-up"];
delete ALIASES["chevron-down"];
delete ALIASES["chevron-left"];
delete ALIASES["chevron-right"];

function resolve(name: string): ReactNode | null {
  if (ICONS[name]) return ICONS[name];
  const alias = ALIASES[name];
  if (alias && ICONS[alias]) return ICONS[alias];
  return null;
}

export function Icon({
  name,
  size = 16,
  stroke = 1.6,
  ...rest
}: IconProps) {
  const payload = resolve(name);
  if (payload === null) {
    // Visible fallback — square with "?" glyph so missing icons are obvious.
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        {...rest}
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <text
          x="12"
          y="16"
          textAnchor="middle"
          fontSize="12"
          fontFamily="var(--font-mono, monospace)"
          fill="currentColor"
          stroke="none"
        >
          ?
        </text>
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {payload}
    </svg>
  );
}
